import axios from "axios";
import type {
    DynamicFormSchemaItem,
    DynamicFormSchemaItemTypes,
    DynamicFormSelectItem,
} from "@/components";
import { makeConfigApi } from "./makeConfigApi";

export type CustomDataConfigurationId = string & { __brand: "CustomDataConfigurationId" };
export type CustomDataConfiguration = {
    id: CustomDataConfigurationId;
    name: string;
    schema: CustomDataField[];
};
export type DbCustomDataConfiguration = {
    id: CustomDataConfigurationId;
    name: string;
    schema: DbCustomDataField[];
};

// Values are the keys the field dialog has always written into a field's rules JSON.
export enum CustomDataRuleTypes {
    Unknown = "unknown",
    MinimumLength = "minimumLength",
    MaximumLength = "maximumLength",
    MinimumValue = "minimumValue",
    MaximumValue = "maximumValue",
    Required = "required",
}

export type CustomDataRule = {
    type: CustomDataRuleTypes;
    value: string | number | boolean | null;
};

export enum CustomDataPortalVisibility {
    Hidden = 0,
    Visible = 1,
    Editable = 2,
}

export type CustomDataFieldId = string & { __brand: "CustomDataFieldId" };
export type CustomFieldDynamicFormSchemaItem = Omit<DynamicFormSchemaItem, "rules"> & {
    rules?: CustomDataRule[];
};
export type DbCustomFieldDynamicFormSchemaItem = Omit<DynamicFormSchemaItem, "rules"> & {
    rules?: string | null;
};
export interface CustomDataField extends CustomFieldDynamicFormSchemaItem {
    id: CustomDataFieldId;
    name: string;
    order: number;
    usedBy?: number;
    rules: CustomDataRule[];
    portalVisibility: CustomDataPortalVisibility;
}
export interface DbCustomDataField extends DbCustomFieldDynamicFormSchemaItem {
    id: CustomDataFieldId;
    name: string;
    order: number;
    usedBy?: number;
    rules: string | null;
    portalVisibility: CustomDataPortalVisibility;
}

const url = "/api/v0/project-configurations/customData";

export type CustomDataConfigApi = {
    getCustomDataConfigurations(): Promise<CustomDataConfiguration[]>;
    createCustomDataConfiguration(name: string): Promise<CustomDataConfiguration>;
    editCustomDataConfiguration(config: CustomDataConfigurationId, name: string): Promise<void>;
    deleteCustomDataConfiguration(config: CustomDataConfigurationId): Promise<void>;
    createCustomDataField(
        config: CustomDataConfigurationId,
        name: string,
        fieldName: string,
        type: DynamicFormSchemaItemTypes,
        options?: DynamicFormSelectItem[],
        rules?: CustomDataRule[],
        portalVisibility?: CustomDataPortalVisibility
    ): Promise<CustomDataField>;
    editCustomDataField(
        config: CustomDataConfigurationId,
        field: CustomDataFieldId,
        name?: string,
        fieldName?: string,
        type?: DynamicFormSchemaItemTypes,
        options?: DynamicFormSelectItem[],
        rules?: CustomDataRule[],
        portalVisibility?: CustomDataPortalVisibility
    ): Promise<void>;
    reorderCustomDataFields(
        config: CustomDataConfigurationId,
        fieldIds: CustomDataFieldId[]
    ): Promise<void>;
    deleteCustomDataField(
        config: CustomDataConfigurationId,
        field: CustomDataFieldId,
        clearValues?: boolean
    ): Promise<void>;
};

const configApi = makeConfigApi<CustomDataConfiguration>("customData");

export const customDataConfigApi: CustomDataConfigApi = {
    async getCustomDataConfigurations() {
        const { data: configs } = await axios.get<DbCustomDataConfiguration[]>(`${url}`);
        return configs.map<CustomDataConfiguration>((c) => ({
            id: c.id,
            name: c.name,
            schema: c.schema.map((s) => ({
                id: s.id,
                name: s.name,
                fieldName: s.fieldName,
                type: s.type,
                order: s.order,
                usedBy: s.usedBy ?? 0,
                rules: JSON.parse(s.rules ?? "[]") as CustomDataRule[],
                options: s.options ?? [],
                portalVisibility: s.portalVisibility,
            })),
        }));
    },

    createCustomDataConfiguration: configApi.create,
    editCustomDataConfiguration: configApi.edit,
    deleteCustomDataConfiguration: configApi.remove,

    async createCustomDataField(config, name, fieldName, type, options, rules, portalVisibility) {
        const { data: configuration } = await axios.post<DbCustomDataField>(
            `${url}/${config}/fields`,
            {
                name,
                fieldName,
                type,
                options,
                rules: JSON.stringify(rules),
                portalVisibility,
            }
        );

        return {
            id: configuration.id,
            name: configuration.name,
            fieldName: configuration.fieldName,
            type: configuration.type,
            order: configuration.order,
            options: configuration.options ?? [],
            rules: JSON.parse(configuration.rules ?? "") as CustomDataRule[],
            portalVisibility: configuration.portalVisibility,
        };
    },

    async editCustomDataField(
        config,
        field,
        name,
        fieldName,
        type,
        options,
        rules,
        portalVisibility
    ) {
        await axios.put(`${url}/${config}/fields/${field}`, {
            name,
            fieldName,
            type,
            options,
            rules: JSON.stringify(rules),
            portalVisibility,
        });
    },

    async reorderCustomDataFields(config, fieldIds) {
        await axios.put(`${url}/${config}/fields/order`, fieldIds);
    },

    async deleteCustomDataField(config, field, clearValues = false) {
        await axios.delete(`${url}/${config}/fields/${field}`, { params: { clearValues } });
    },
};
